import {
  Integration,
  MerchantCashAdvance,
  MerchantCashAdvanceDocument,
  MerchantCashAdvanceStatusHistoryDocument,
  Rights,
  Transaction,
  TransactionStatus,
  TransactionType,
  useCancelTransactionMutation,
  useDeleteManualTransactionMutation,
  useMcaTransactionsQuery,
  useRefreshMerchantCashAdvanceTransactionsMutation,
  useStopTransactionMutation,
  useSaveTransactionTypeMutation,
  GridType
} from '../../graphql/types';
import { InteractionOutlined, BarsOutlined, LinkOutlined } from '@ant-design/icons';
import { Backdrop, Box, Button, Tooltip, CircularProgress, Dialog, LinearProgress, Stack } from '@mui/material';
import 'ag-grid-enterprise/styles/ag-grid.css';
import 'ag-grid-enterprise/styles/ag-theme-material.css';
import 'assets/third-party/ag-theme-material-dark.scss';
import useConfig from '../../hooks/useConfig';
import React, { useCallback, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { DefaultAgGridTooltip } from '../../components/ag-grid/DefaultAgGridTooltip';
import MainCard from '../../components/MainCard';
import { useTheme } from '@mui/material/styles';
import { ExportOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  SortChangedEvent,
  GetContextMenuItemsParams,
  GridReadyEvent,
  MenuItemDef,
  ColDef,
  FilterChangedEvent
} from 'ag-grid-community';
import { GridApi, ICellRendererParams, SideBarDef } from 'ag-grid-enterprise';
import { getFormattedDateUTC } from '../../utils/getFormattedDate';
import AddManualTransaction from './AddManualTransaction';
import { dispatch } from '../../store';
import { openSnackbar } from '../../store/reducers/snackbar';
import { getErrorMessage, requireMutationData } from '../../utils/graphqlMutationResult';
import ActionConfirmation from '../../components/mca/ActionConfirmation';
import useAuth from '../../hooks/useAuth';
import EditTransactionNote from './EditTransactionNote';
import { CurrencyText } from '../../components/input-formatters/CurrencyText';
import { calculateSyndicationPercent } from './SyndicationDetails';
import { McaTransactionStatusChip } from './McaTransactionStatusChip';
import PlatformCompanyLogo from '../../utils/platformCompanyLogo';
import {
  TransactionAmountCell,
  TransactionDefaultTypeCell,
  TransactionStatusCell
} from '../../components/ag-grid/AgGridTransactionsChipCell';
import { reportApi } from '../../api/reportApi';
import { allowedEditableTransactionTypes } from '../../pages/transactions/transactions-list';
import McaTransactionsContextMenu from './MCATransactionsContextMenu';
import { Icon, supportedTransactionTypesforIcons, supportedPaymentMethodIdsforIcons } from '../../components/icons';
import TransactionsTypeLogo from '../../utils/transactionsTypeLogo';
import PaymentMethodsLogo from '../../utils/paymentMethodLogo';
import { underscoreToSpace } from '../../utils/strings';
import TransactionsActionsCellRenderer from '../../components/TransactionsActionsCellRenderer';
import useAgGridOutsideClick from '../../components/ag-grid/useAgGridOutsideClick';
import McaListTools from './McaListTools';
import { applyGridColumnState } from '../../utils/agGridUtils';
import CustomTooltip from 'components/CustomTooltip';

export const StatusCell = ({ value, node, data }: ICellRendererParams) => {
  if (node.isRowPinned()) {
    return null;
  }
  return <McaTransactionStatusChip transaction={data} />;
};

export const clearedTransactions = [TransactionStatus.Cleared];
export const manualTransactions = [
  TransactionType.Collection,
  TransactionType.Refund,
  TransactionType.DefaultFee,
  TransactionType.Expense,
  TransactionType.OtherFee
];

interface Props {
  mca: MerchantCashAdvance;
}

interface McaTransactionsForwardRef {
  refresh: () => void;
  reloadTransactionsFromProcessor: () => void;
}

export const sharedCompactViewKey = `MCA_TABS_COMPACTVIEW`;
export const mcaTransactionsKeyName = 'MCA_TRANSACTION_GRID';
const McaTransactions = forwardRef<McaTransactionsForwardRef, Props>(({ mca }, ref) => {
  const theme = useTheme();
  const { mode } = useConfig();
  const { user: currentUser } = useAuth();
  const userId = currentUser?.id;
  const keyName = `${userId}_${mcaTransactionsKeyName}`;
  const compactViewKey = `${userId}_${sharedCompactViewKey}`;
  const useCompactView = localStorage.getItem(compactViewKey + '_USE_COMPACT_VIEW') === 'false' ? null : true;
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const isSuperAdmin = currentUser?.rights.includes(Rights.IsSuperAdmin);
  const canManage = currentUser?.rights.includes(Rights.ManageMcaTransactions) && mca.currentUserRights.includes(Rights.EditMca);
  const syndication = mca.syndications?.find((s) => s.userId === currentUser?.id);
  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);
  const { loading, data, refetch } = useMcaTransactionsQuery({
    variables: { id: mca.id },
    fetchPolicy: 'network-only'
  });
  const [updateTransactionType] = useSaveTransactionTypeMutation();
  const transactions = useMemo(() => (data?.mcaTransactions || []) as Transaction[], [data]);
  const [cancelTransaction] = useCancelTransactionMutation();
  const [deleteManualTransaction] = useDeleteManualTransactionMutation();
  const [stopTransaction] = useStopTransactionMutation();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [add, setAdd] = useState<boolean>(false);
  const handleAdd = () => {
    setAdd(!add);
    if (transaction && !add) setTransaction(null);
  };
  const [editNote, setEditNote] = useState<boolean>(false);
  const handleEditNote = () => {
    setEditNote(!editNote);
    if (transaction && !editNote) setTransaction(null);
  };
  const [refreshMerchantCashAdvanceTransactionsMutation] = useRefreshMerchantCashAdvanceTransactionsMutation();
  const [backdropOpen, setBackdropOpen] = useState<boolean>(false);
  const [openCancelConfirmation, setOpenCancelConfirmation] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const handleCancelTransaction = async (approved: boolean) => {
    setOpenCancelConfirmation(false);
    if (!approved || !transaction) {
      return;
    }
    setBackdropOpen(true);
    try {
      if (manualTransactions.includes(transaction.type)) {
        const result = await cancelTransaction({
          variables: { id: transaction.id },
          refetchQueries: [
            { query: MerchantCashAdvanceDocument, variables: { id: mca?.id } },
            { query: MerchantCashAdvanceStatusHistoryDocument, variables: { id: mca?.id } }
          ]
        });
        requireMutationData(result, (data) => data?.cancelTransaction, 'Transaction was not canceled.');
      } else {
        const result = await stopTransaction({
          variables: {
            transactionId: transaction.id
          },
          refetchQueries: [
            { query: MerchantCashAdvanceDocument, variables: { id: mca?.id } },
            { query: MerchantCashAdvanceStatusHistoryDocument, variables: { id: mca?.id } }
          ]
        });
        requireMutationData(result, (data) => data?.stopTransaction, 'Transaction was not canceled.');
      }
      dispatch(
        openSnackbar({
          open: true,
          message: 'Transaction canceled successfully.',
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
      refetch();
    } catch (error) {
      dispatch(
        openSnackbar({
          open: true,
          message: getErrorMessage(error, 'Failed to cancel transaction.'),
          variant: 'alert',
          alert: { color: 'error' },
          close: true
        })
      );
    } finally {
      setBackdropOpen(false);
    }
  };
  const handleDeleteTransaction = async (approved: boolean) => {
    setOpenDeleteConfirmation(false);
    if (!approved || !transaction) {
      return;
    }
    setBackdropOpen(true);
    try {
      const result = await deleteManualTransaction({
        variables: { id: transaction?.id },
        refetchQueries: [
          { query: MerchantCashAdvanceDocument, variables: { id: mca?.id } },
          { query: MerchantCashAdvanceStatusHistoryDocument, variables: { id: mca?.id } }
        ]
      });
      requireMutationData(result, (data) => data?.deleteManualTransaction, 'Transaction was not deleted.');
      dispatch(
        openSnackbar({
          open: true,
          message: 'Transaction deleted successfully.',
          variant: 'alert',
          alert: { color: 'success' },
          close: true
        })
      );
    } catch (error) {
      dispatch(
        openSnackbar({
          open: true,
          message: getErrorMessage(error, 'Failed to delete transaction.'),
          variant: 'alert',
          alert: { color: 'error' },
          close: true
        })
      );
    } finally {
      setBackdropOpen(false);
    }
    refetch();
  };

  const handleRefetch = async () => {
    setBackdropOpen(true);
    try {
      const result = await refreshMerchantCashAdvanceTransactionsMutation({
        variables: {
          mcaId: mca.id
        }
      });
      requireMutationData(result, (data) => data?.refreshMerchantCashAdvanceTransactions, 'Transactions were not reloaded.');
      dispatch(
        openSnackbar({
          open: true,
          message: 'Transactions have been reloaded from processors.',
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: true
        })
      );
      refetch();
    } catch (error) {
      dispatch(
        openSnackbar({
          open: true,
          message: `Failed to reload transactions: ${getErrorMessage(error, 'Operation failed')}`,
          variant: 'alert',
          alert: {
            color: 'error'
          },
          close: true
        })
      );
    } finally {
      setBackdropOpen(false);
    }
  };

  useImperativeHandle(ref, () => ({
    refresh: () => refetch(),
    reloadTransactionsFromProcessor: () => handleRefetch()
  }));

  const refreshGrid = useCallback(() => {
    if (gridApi) {
      gridApi.refreshServerSide();
    }
    setRefreshKey((prevKey) => prevKey + 1);
  }, [gridApi]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      editable: false,
      sortable: true,
      resizable: true,
      pivot: true,
      filter: false,
      cellStyle: {
        display: 'flex',
        alignItems: 'center'
      },
      suppressHeaderMenuButton: true,
      columnChooserParams: {
        suppressColumnFilter: true,
        contractColumnSelection: true
      },
      tooltipComponent: DefaultAgGridTooltip
    };
  }, []);

  const sideBar = useMemo<SideBarDef>(() => {
    return {
      position: 'right',
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: true,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true
          }
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
          toolPanelParams: {
            suppressFilterSearch: true
          }
        },
        {
          id: 'settings',
          labelDefault: 'Settings',
          labelKey: 'settings',
          iconKey: 'menu',
          toolPanel: McaListTools,
          toolPanelParams: {
            preferencesKey: GridType.McaTransactionsGrid,
            displayInfiniteScrollToggle: false,
            displayCompactViewToggle: true,
            sharedKeyName: sharedCompactViewKey,
            refreshGrid,
            keyName
          }
        }
      ]
    };
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        suppressHeaderMenuButton: true,
        headerName: '#',
        field: 'number',
        cellClass: 'ag-centered-cell',
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center']
      },
      {
        suppressHeaderMenuButton: true,
        sortable: false,
        headerName: 'Platform',
        cellClass: 'ag-centered-cell',
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        headerComponent: () => {
          return (
            <CustomTooltip
              title="Platform"
            >
              <InteractionOutlined style={{ fontSize: 20 }} />
            </CustomTooltip>
          );
        },
        field: 'integration.name',
        tooltipValueGetter: ({ data }) => {
          return data.integration?.name ? data.integration.name : data?.paymentMethod?.id ? underscoreToSpace(data.paymentMethod.id) : '';
        },
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          const CenterWrapper = ({ children }: { children: React.ReactNode }) => (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}
            >
              {children}
            </Box>
          );
          if (!data.integration) {
            if (data?.type === TransactionType.Collection) {
              if (supportedPaymentMethodIdsforIcons.includes(data?.paymentMethod?.id)) {
                return (
                  <CenterWrapper>
                    <Tooltip title={data?.paymentMethod?.id}>
                      <span className="ag-centered-cell">
                        <Icon id={data?.paymentMethod?.id} size="xxs" />
                      </span>
                    </Tooltip>
                  </CenterWrapper>
                );
              } else {
                return (
                  <CenterWrapper>
                    <span>
                      <PaymentMethodsLogo paymentMethod={data?.paymentMethod} size={'xs'} mt={0} />
                    </span>
                  </CenterWrapper>
                );
              }
            }
            if (supportedTransactionTypesforIcons.includes(data?.type)) {
              return (
                <CenterWrapper>
                  <Tooltip title={data?.type}>
                    <span className="ag-centered-cell">
                      <Icon id={data?.paymentMethod?.id} size="xxs" />
                    </span>
                  </Tooltip>
                </CenterWrapper>
              );
            } else {
              return (
                <CenterWrapper>
                  <span>
                    <TransactionsTypeLogo type={data?.type} size={'xs'} mt={0} />
                  </span>
                </CenterWrapper>
              );
            }
          } else {
            return (
              <CenterWrapper>
                <span>
                  <PlatformCompanyLogo integration={data?.integration as Integration} size={'xs'} mt={0} />
                </span>
              </CenterWrapper>
            );
          }
        }
      },
      {
        headerName: 'Name',
        field: 'name',
        tooltipField: 'name',
        headerClass: 'ag-header-cell-merchant-group',
        toolPanelClass: ['ag-header-cell-merchant-group']
      },
      {
        initialHide: true,
        headerName: 'Origination Date',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        field: 'originationDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.originationDate)
      },
      {
        headerName: 'Effective Date',
        suppressHeaderMenuButton: true,
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        sort: 'desc',
        field: 'effectiveDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.effectiveDate),
        comparator: (valueA, valueB, nodeA, nodeB, isDescending) => {
          let dateA = new Date(valueA).getTime();
          let dateB = new Date(valueB).getTime();
          if (dateA === dateB) return 0;
          return dateA > dateB ? 1 : -1;
        }
      },
      {
        headerName: 'Type',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        field: 'type',
        filter: true,
        cellRenderer: TransactionDefaultTypeCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        cellStyle: {
          display: 'flex',
          alignItems: 'center'
        }
      },
      {
        initialHide: true,
        headerName: 'Pmt Method',
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['ag-header-performance-group-right'],
        field: 'paymentMethod.id',
        cellClass: 'ag-centered-cell',
        sortable: false,
        tooltipValueGetter: (params) => {
          const id = params.data?.paymentMethod?.id;
          return id ? underscoreToSpace(id) : 'UNKNOWN';
        },
        cellRenderer: ({ data, node }: ICellRendererParams) => {
          if (node.isRowPinned()) {
            return null;
          }
          if (supportedPaymentMethodIdsforIcons.includes(data?.paymentMethod?.id)) {
            return (
              <Tooltip content={data?.paymentMethod?.id}>
                <span className="ag-centered-cell">
                  <Icon id={data?.paymentMethod?.id} size="xxs" />
                </span>
              </Tooltip>
            );
          } else {
            return (
              <span>
                <PaymentMethodsLogo paymentMethod={data?.paymentMethod} size={'xs'} mt={0} />
              </span>
            );
          }
        }
      },
      {
        headerName: 'Account',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        field: 'account',
        cellClass: 'textFormat'
      },
      {
        headerName: 'Status',
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['ag-header-performance-group'],
        field: 'status',
        filter: true,
        cellRenderer: TransactionStatusCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        cellClass: 'ag-centered-cell'
      },
      {
        headerName: 'Amount',
        headerClass: 'text-center-lightskyblue',
        toolPanelClass: ['ag-header-performance-group'],
        field: 'amount',
        cellRenderer: TransactionAmountCell,
        cellRendererParams: { useCompactViewStyle: useCompactView ? true : false },
        type: 'rightAligned',
        cellClass: 'ag-centered-cell'
      },
      {
        hide: !syndication,
        initialHide: !syndication,
        suppressColumnsToolPanel: !syndication,
        headerName: 'My Amount',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        field: 'amount',
        cellRenderer: ({ data }: ICellRendererParams) => {
          return <>{syndication && <CurrencyText value={(data.amount * calculateSyndicationPercent(mca, syndication)) / 100} />}</>;
        },
        type: 'rightAligned'
      },
      {
        headerName: 'Deposit Date',
        headerClass: 'ag-header-performance-group-right',
        toolPanelClass: ['ag-header-performance-group-right'],
        field: 'depositDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.depositDate)
      },
      {
        initialHide: true,
        headerName: 'Notes',
        field: 'notes',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        tooltipField: 'notes'
      },
      {
        initialHide: true,
        headerName: 'To',
        field: 'to',
        headerClass: 'ag-header-performance-group',
        toolPanelClass: ['ag-header-performance-group'],
        tooltipField: 'to'
      },
      {
        initialHide: true,
        headerName: 'Return Date',
        headerClass: 'lightsalmon',
        toolPanelClass: ['ag-header-fees-group-right'],
        field: 'returnDate',
        type: 'rightAligned',
        valueGetter: (params) => getFormattedDateUTC(params.data?.returnDate)
      },
      {
        headerName: 'Return Code',
        headerClass: 'lightsalmon',
        toolPanelClass: ['ag-header-fees-group-right'],
        field: 'returnCode'
      },
      {
        initialHide: true,
        headerName: 'Recurrence Description',
        headerClass: 'ag-header-transaction-cell',
        toolPanelClass: ['ag-header-transaction-cell'],

        field: 'recurrenceDescription',
        tooltipField: 'recurrenceDescription'
      },
      {
        initialHide: true,
        headerName: 'Transaction ID',
        headerComponent: () => {
          return (
            <CustomTooltip
              title="Transaction ID"
            >
              <LinkOutlined style={{ fontSize: 20 }} />
            </CustomTooltip>
          );
        },
        headerClass: 'ag-header-transaction-cell-center',
        toolPanelClass: ['ag-header-transaction-cell-center'],
        field: 'externalId',
        tooltipField: 'externalId'
      },
      {
        field: 'actions',
        headerName: '',
        hide: !canManage,
        suppressColumnsToolPanel: true,
        headerComponent: () => {
          return (
            <CustomTooltip
              title="Actions"
            >
              <BarsOutlined style={{ fontSize: 20, display: 'block', margin: 'auto' }} />
            </CustomTooltip>
          );
        },
        cellRenderer: (params: ICellRendererParams) => (
          <TransactionsActionsCellRenderer
            data={params.data}
            node={params.node}
            setTransaction={setTransaction}
            handleEditNote={handleEditNote}
            handleManualEdit={handleAdd}
            setOpenCancelConfirmation={setOpenCancelConfirmation}
            setOpenDeleteConfirmation={setOpenDeleteConfirmation}
            isSuperAdmin={isSuperAdmin}
            manualTransactions={manualTransactions}
          />
        ),
        sortable: false,
        enablePivot: false,
        pivot: false,
        suppressNavigable: true,
        suppressHeaderMenuButton: true,
        aggFunc: null,
        cellClass: 'no-border'
      }
    ],
    [useCompactView]
  );

  useEffect(() => {
    if (!gridApi) {
      return;
    }
    gridApi.setGridOption('pinnedBottomRowData', [
      {
        amount: data?.mcaTransactions.reduce((accumulator, transaction) => {
          return accumulator + (clearedTransactions.includes(transaction.status) ? transaction.amount || 0 : 0);
        }, 0)
      }
    ]);
  }, [data, gridApi]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    applyGridColumnState(params.api, keyName);
  }, []);
  const onRowDataUpdated = useCallback(() => {
    if (!gridApi) return;
    applyGridColumnState(gridApi, keyName);
  }, [gridApi]);

  type excelTypes = {
    id: string;
    dataType: any;
  };
  useAgGridOutsideClick(gridApi, true, 'mca-transactions-list');
  const excelStyles: excelTypes[] = [
    {
      id: 'textFormat',
      dataType: 'String'
    }
  ];

  const onFilterChanged = useCallback(({ api }: FilterChangedEvent) => {
    let amountSum = 0;
    api.forEachNodeAfterFilter((rowNode) => {
      if (rowNode.data && clearedTransactions.includes(rowNode.data.status)) amountSum += rowNode.data.amount;
    });
    api.setGridOption('pinnedBottomRowData', [{ amount: amountSum }]);
  }, []);

  const pinnedBottomRowData = useMemo<any[]>(() => {
    return [{ amount: 0 }];
  }, []);

  const canReload = !!(mca.recurrencies?.length);

  const handleExportReport = useCallback(async () => {
    setBackdropOpen(true);
    const qp = {
      mcaId: mca.id
    };
    await reportApi.handleExportReport('mca-transactions', qp);
    setBackdropOpen(false);
  }, [mca.id]);

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (MenuItemDef | string)[] => {
      return McaTransactionsContextMenu({
        params,
        updateTransactionType,
        mcaId: mca.id,
        isSuperAdmin,
        allowedEditableTransactionTypes
      });
    },
    [isSuperAdmin, mca.id, updateTransactionType]
  );

  const saveColumnsStateOnChange = useCallback(
    (event: ColumnResizedEvent | SortChangedEvent | ColumnMovedEvent | ColumnVisibleEvent) => {
      if (
        gridApi && event.source?.toLowerCase().includes('ui') &&
        ((event.type === 'columnResized' && event.finished) ||
          (event.type === 'columnMoved' && event.finished) ||
          event.type === 'sortChanged' ||
          event.type === 'columnVisible')
      ) {
        if (event.type === 'columnVisible') {
          const colId = event.column?.getColId();
          if (colId && event.column?.isVisible()) {
            gridApi.autoSizeColumns([colId], true);
          }
        }
        const columnsState = JSON.stringify(gridApi.getColumnState());
        localStorage.setItem(keyName + '_COLUMNS_STATE', columnsState);
      }
    },
    [gridApi]
  );

  const getRowClass = useCallback(
    (params) => {
      if (params.node.rowPinned === 'bottom') {
        return 'ag-pin-bottom-row-' + mode;
      }
    },
    [mode]
  );
  return (
    <Box display="flex" flexDirection="column">
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={backdropOpen}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <MainCard border={false} boxShadow shadow={theme.customShadows.z1} sx={{ height: '100%', p: '8px 16px 16px', ml: -0.5, mr: 0.5 }} content={false}>
        {loading && <LinearProgress />}
        <Stack direction={'row'} pb={1} justifyContent="flex-end" alignItems="center" spacing={1}>
          {canManage && (
            <>
              <CustomTooltip title="Add Transaction">
                <Button variant="contained" onClick={handleAdd} sx={{ fontSize: '12px', minWidth: 'auto' }}>
                  <PlusOutlined />
                </Button>
              </CustomTooltip>
              {canReload && (
                <CustomTooltip title="Update">
                  <Button variant="contained" onClick={handleRefetch} sx={{ fontSize: '12px', minWidth: 'auto' }}>
                    <ReloadOutlined />
                  </Button>
                </CustomTooltip>
              )}
            </>
          )}
          {canManage && (
            <CustomTooltip title="Export Report">
              <Button variant="contained" onClick={handleExportReport} sx={{ fontSize: '12px', minWidth: 'auto' }}>
                <ExportOutlined />
              </Button>
            </CustomTooltip>
          )}
        </Stack>
        <div
          id="mca-transactions-list"
          className={`${mode === 'light' ? 'ag-theme-material' : 'ag-theme-material-dark'} ${useCompactView ? 'compact-view' : 'expanded-view'
            }`}
          style={gridStyle}
        >
          <AgGridReact<Transaction[]>
            key={refreshKey}
            onRowDataUpdated={onRowDataUpdated}
            domLayout={'autoHeight'}
            sideBar={sideBar}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowData={transactions}
            onGridReady={onGridReady}
            onFilterChanged={onFilterChanged}
            onColumnResized={saveColumnsStateOnChange}
            onSortChanged={saveColumnsStateOnChange}
            onColumnMoved={saveColumnsStateOnChange}
            onColumnVisible={saveColumnsStateOnChange}
            pinnedBottomRowData={pinnedBottomRowData}
            suppressColumnVirtualisation={true}
            getContextMenuItems={getContextMenuItems}
            excelStyles={excelStyles}
            getRowClass={getRowClass}
          ></AgGridReact>
        </div>
      </MainCard>

      {/* add manual transaction dialog */}
      <Dialog maxWidth="sm" fullWidth onClose={handleAdd} open={add} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        {add && <AddManualTransaction mca={mca} entity={transaction} onCancel={handleAdd} onSave={(transaction) => refetch()} />}
      </Dialog>
      {/* edit transaction note dialog */}
      <Dialog maxWidth="sm" fullWidth onClose={handleEditNote} open={editNote} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
        {transaction && (
          <EditTransactionNote mca={mca} entity={transaction} onCancel={handleEditNote} onSave={(transaction) => refetch()} />
        )}
      </Dialog>
      {openCancelConfirmation && (
        <ActionConfirmation
          title={`Confirm transaction ${transaction?.externalId || transaction?.id} canceling`}
          open={openCancelConfirmation}
          type="error"
          handleClose={handleCancelTransaction}
        ></ActionConfirmation>
      )}
      {openDeleteConfirmation && (
        <ActionConfirmation
          title={`Deleted transactions cannot be restored. Would you like to proceed?`}
          open={openDeleteConfirmation}
          useYesNoButtonLabels={true}
          type="error"
          handleClose={handleDeleteTransaction}
        ></ActionConfirmation>
      )}
    </Box>
  );
});

export default McaTransactions;
